'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faRoute, faXmark } from '@fortawesome/pro-solid-svg-icons';
import useRouteState from 'src/hooks/routes/useRouteState';
import useRouteActions from 'src/hooks/routes/useRouteActions';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { twMerge } from 'tailwind-merge';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import { clearPendingProvider } from 'src/redux';
import { RouteListPanel } from '../RouteListPanel';
import { RouteDetailPanel } from '../RouteDetailPanel';
import { RouteEditor } from './components';
import { ROUTES_DRAWER_ID } from './constants';

interface RoutesDrawerProps {
  /** Optional view mode - when provided, drawer auto-closes on list view */
  view?: 'map' | 'list';
}

const RoutesDrawer: React.FC<RoutesDrawerProps> = ({ view }) => {
  const dispatch = useDispatch();
  const { isCreating, isEditing, isViewing } = useRouteState();
  const { startCreate, cancel } = useRouteActions();
  const { isDrawerOpen, closeAllButCurrent, closeDrawer } = useDrawer();
  const [mounted, setMounted] = useState(false);

  const isOpen = isDrawerOpen(ROUTES_DRAWER_ID);

  // Handle mounting for portal
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Auto-close drawer when switching to list view (only when view prop is provided)
  useEffect(() => {
    if (!view || view === 'map') return;

    closeDrawer(ROUTES_DRAWER_ID);
  }, [view, closeDrawer]);

  const handleCollapse = () => {
    dispatch(clearPendingProvider());
    closeDrawer(ROUTES_DRAWER_ID);
  };

  const handleToggleRoute = () => {
    if (isCreating || isEditing || isViewing) {
      cancel();
      closeAllButCurrent(ROUTES_DRAWER_ID);
    } else {
      closeAllButCurrent(ROUTES_DRAWER_ID);
      startCreate();
    }
  };

  const isEditorActive = isCreating || isEditing;
  const isListActive = !isEditorActive && !isViewing;

  // Don't render on server or when not mounted
  if (!mounted) {
    return null;
  }

  const drawerContent = (
    <div
      className={twMerge(
        'fixed top-0 left-0 bottom-0 z-[9999] w-80 bg-white shadow-xl flex flex-col transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}
      data-testid="routes-drawer"
      aria-label="Routes drawer"
      aria-hidden={!isOpen}
    >
      {/* Header */}
      <div className="p-4 flex items-start justify-between shrink-0 border-b border-sky-200 bg-sky-50">
        <div className="flex items-center gap-2">
          <Icon icon={faRoute} className="w-4 h-4 text-sky-600" />
          <h3 className="text-sm font-semibold text-sky-900">Routes</h3>
        </div>
        <PulseIconButton
          variant="plain"
          color="neutral"
          size="sm"
          onClick={handleCollapse}
          icon={faXmark}
          aria-label="Close routes drawer"
          data-testid="routes-drawer-close-button"
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto bg-white">
        {isEditorActive ? (
          <RouteEditor />
        ) : isViewing ? (
          <RouteDetailPanel />
        ) : (
          <RouteListPanel className="flex-1 min-h-0 overflow-hidden" />
        )}
      </div>

      {/* Footer with Create Route button when in list mode */}
      {isListActive && (
        <div className="border-t border-neutral-200 px-4 py-3 shrink-0 bg-neutral-50">
          <PulseButton
            variant="solid"
            color="primary"
            onClick={handleToggleRoute}
            className="w-full"
            data-testid="routes-drawer-create-route-button"
            startDecorator={faRoute}
          >
            Create Route
          </PulseButton>
        </div>
      )}
    </div>
  );

  return createPortal(drawerContent, document.body);
};

export default RoutesDrawer;
